import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { Order, User, EmailLog } from '../types';
import { api, downloadExport } from '../api/client';
import { fromISO, formatDateLong } from '../utils/dates';
import { exportToCSV } from '../utils/csv';
import { MenuIngestionModal } from '../components/MenuIngestionModal';
import { useSettings } from '../contexts/SettingsContext';

interface AdminPageProps {
  currentUser: User;
  addToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

export function AdminPage({ currentUser, addToast }: AdminPageProps) {
  const { pickupEnabled } = useSettings();
  const [activeTab, setActiveTab] = useState<'orders' | 'mails' | 'billing'>('orders');

  const [orders, setOrders] = useState<Order[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showIngestion, setShowIngestion] = useState(false);

  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterSearch, setFilterSearch] = useState('');

  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [selectedOrder, setSelectedOrder] = useState<EnrichedOrder | null>(null);

  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);

  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [billingMonth, setBillingMonth] = useState(defaultMonth);
  const [billingLoading, setBillingLoading] = useState(false);
  const [reconciliationLoading, setReconciliationLoading] = useState(false);
  const [costCenterLoading, setCostCenterLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [ordersData, usersData] = await Promise.all([
        api.orders.getAll({
          from: filterFrom || undefined,
          to: filterTo || undefined,
        }),
        api.users.getAll(),
      ]);
      setOrders(ordersData);
      setUsers(usersData);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ladefehler';
      addToast(`Fehler: ${message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [filterFrom, filterTo, addToast]);

  const loadEmailLogs = useCallback(async () => {
    setEmailLogsLoading(true);
    try {
      const data = await api.emailLogs.getAll();
      setEmailLogs(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Ladefehler';
      addToast(`Fehler beim Laden der Mail-Logs: ${message}`, 'error');
    } finally {
      setEmailLogsLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (activeTab === 'mails') {
      loadEmailLogs();
    }
  }, [activeTab, loadEmailLogs]);

  if (!currentUser.isAdmin) {
    return (
      <div className="loading-center" style={{ minHeight: '12.5rem' }}>
        <div style={{ fontSize: '2.25rem' }}>🔒</div>
        <div style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Kein Zugriff</div>
      </div>
    );
  }

  const userMap = new Map(users.map((u) => [u.id, u]));

  const companies = [...new Set(users.map((u) => u.company))].sort();

  interface EnrichedOrder {
    order: Order;
    date: string;
    recipientLastName: string;
    recipientFirstName: string;
    company: string;
    dishName: string;
    pickupCode: string;
    redeemed: boolean;
  }

  const buildEnrichedOrders = (): EnrichedOrder[] => {
    return orders.map((order) => {
      const user = userMap.get(order.userId);
      let recipientLastName = '';
      let recipientFirstName = '';
      let company = user?.company || '';
      let pickupCode = '';

      if (order.recipientType === 'self') {
        recipientLastName = user?.lastName || '';
        recipientFirstName = user?.firstName || '';
        pickupCode = user?.pickupCode || '';
      } else if (order.recipientType === 'colleague') {
        const colleague = userMap.get(order.colleagueId || '');
        recipientLastName = colleague?.lastName || '';
        recipientFirstName = colleague?.firstName || '';
        company = colleague?.company || company;
        pickupCode = colleague?.pickupCode || '';
      } else if (order.recipientType === 'guest') {
        const parts = (order.recipientName || '').split(' ');
        recipientFirstName = parts[0] || '';
        recipientLastName = parts.slice(1).join(' ') || parts[0] || '';
        pickupCode = order.guestCode || '';
      }

      return {
        order,
        date: order.dateISO,
        recipientLastName,
        recipientFirstName,
        company,
        dishName: order.dish.name,
        pickupCode,
        redeemed: order.redeemed,
      };
    });
  };

  const filtered = buildEnrichedOrders().filter((row) => {
    if (filterCompany && row.company !== filterCompany) return false;
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      const searchable = [
        row.recipientFirstName, row.recipientLastName, row.company,
        row.dishName, row.pickupCode, row.order.dateISO,
      ].join(' ').toLowerCase();
      if (!searchable.includes(q)) return false;
    }
    return true;
  });

  const sortedFiltered = [...filtered].sort((a, b) => {
    if (a.date !== b.date) return b.date.localeCompare(a.date);
    if (a.company !== b.company) return a.company.localeCompare(b.company);
    if (a.recipientLastName !== b.recipientLastName) return a.recipientLastName.localeCompare(b.recipientLastName);
    return a.recipientFirstName.localeCompare(b.recipientFirstName);
  });

  const groupedByDay = new Map<string, Map<string, EnrichedOrder[]>>();
  for (const row of sortedFiltered) {
    if (!groupedByDay.has(row.date)) {
      groupedByDay.set(row.date, new Map());
    }
    const companyMap = groupedByDay.get(row.date)!;
    if (!companyMap.has(row.company)) {
      companyMap.set(row.company, []);
    }
    companyMap.get(row.company)!.push(row);
  }

  const toggleDay = (dayKey: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayKey)) {
        next.delete(dayKey);
      } else {
        next.add(dayKey);
      }
      return next;
    });
  };

  const handleExport = () => {
    exportToCSV(
      sortedFiltered.map((r) => r.order),
      users,
      [],
      new Set()
    );
    addToast('CSV-Export gestartet', 'success');
  };

  const handleReset = () => {
    setFilterFrom('');
    setFilterTo('');
    setFilterCompany('');
    setFilterSearch('');
  };

  const handleDownloadBilling = async () => {
    setBillingLoading(true);
    try {
      await downloadExport(`/export/billing?month=${billingMonth}`);
      addToast('Abrechnungs-ZIP wird heruntergeladen', 'success');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
      addToast(`Fehler: ${message}`, 'error');
    } finally {
      setBillingLoading(false);
    }
  };

  const handleDownloadReconciliation = async () => {
    setReconciliationLoading(true);
    try {
      await downloadExport(`/export/reconciliation?month=${billingMonth}`);
      addToast('Abgleichs-CSV wird heruntergeladen', 'success');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
      addToast(`Fehler: ${message}`, 'error');
    } finally {
      setReconciliationLoading(false);
    }
  };

  const handleDownloadCostCenterBilling = async () => {
    setCostCenterLoading(true);
    try {
      await downloadExport(`/export/cost-center-billing?month=${billingMonth}`);
      addToast('Kostenstellen-Abrechnung wird heruntergeladen', 'success');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
      addToast(`Fehler: ${message}`, 'error');
    } finally {
      setCostCenterLoading(false);
    }
  };

  const formatSentAt = (sentAt: string): string => {
    const d = new Date(sentAt);
    return d.toLocaleDateString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '0.625rem' }}>
        <div className="admin-subtabs" style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            className="btn btn-accent-hover btn-sm"
            onClick={() => setActiveTab('orders')}
          >
            Bestellungen
          </button>
          <button
            className="btn btn-accent-hover btn-sm"
            onClick={() => setActiveTab('mails')}
          >
            Mails
          </button>
          <button
            className="btn btn-accent-hover btn-sm"
            onClick={() => setActiveTab('billing')}
          >
            Abrechnung
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          {pickupEnabled && (
            <Link to="/pickup" className="btn btn-secondary">
              🍽️ Essensausgabe öffnen
            </Link>
          )}
          {activeTab === 'orders' && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowIngestion(true)}
            >
              📸 Speiseplan einlesen
            </button>
          )}
        </div>
      </div>

      {activeTab === 'orders' && (
        <>
          <div className="admin-filter-bar">
            <div className="admin-filter-group">
              <label className="admin-filter-label">Von</label>
              <input
                type="date"
                className="admin-filter-input"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
              />
            </div>
            <div className="admin-filter-group">
              <label className="admin-filter-label">Bis</label>
              <input
                type="date"
                className="admin-filter-input"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
              />
            </div>
            <div className="admin-filter-group">
              <label className="admin-filter-label">Unternehmen</label>
              <select
                className="admin-filter-input"
                value={filterCompany}
                onChange={(e) => setFilterCompany(e.target.value)}
                style={{ appearance: 'none', minWidth: 160 }}
              >
                <option value="">Alle</option>
                {companies.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="admin-filter-group" style={{ flex: 1 }}>
              <label className="admin-filter-label">Suche</label>
              <input
                type="text"
                className="admin-filter-input"
                placeholder="Name, Gericht, Code…"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>
            <div className="admin-filter-group" style={{ justifyContent: 'flex-end' }}>
              <label className="admin-filter-label">&nbsp;</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-ghost btn-sm" onClick={handleReset}>
                  Zurücksetzen
                </button>
                <button className="btn btn-primary btn-sm" onClick={handleExport}>
                  ⬇ CSV
                </button>
              </div>
            </div>
          </div>

          <div className="admin-row-count">
            <strong>{sortedFiltered.length}</strong> {sortedFiltered.length === 1 ? 'Bestellung' : 'Bestellungen'} angezeigt
          </div>

          {loading ? (
            <div className="loading-center">
              <div className="spinner" />
              <span>Daten werden geladen…</span>
            </div>
          ) : sortedFiltered.length === 0 ? (
            <div className="admin-table-wrapper">
              <div className="admin-empty">
                Keine Bestellungen gefunden
              </div>
            </div>
          ) : (
            <div className="admin-table-wrapper">
              {[...groupedByDay.entries()].map(([dayKey, companyMap]) => {
                const isExpanded = expandedDays.has(dayKey);
                return (
                  <div key={dayKey} className="admin-day-group">
                    <button
                      type="button"
                      className="admin-day-header"
                      onClick={() => toggleDay(dayKey)}
                      aria-expanded={isExpanded}
                    >
                      <span className={`admin-day-chevron${isExpanded ? ' expanded' : ''}`}>▸</span>
                      <span>{formatDateLong(fromISO(dayKey))}</span>
                    </button>

                    {isExpanded && (
                      <div className="admin-day-body">
                        {[...companyMap.entries()].map(([company, rows]) => (
                          <div key={company}>
                            <div className="admin-company-heading">{company}</div>

                            {rows.map((row) => (
                              <div
                                key={row.order.id}
                                className={`admin-order-row${row.order.cancelled ? ' cancelled' : ''}`}
                              >
                                <span className="admin-order-name">
                                  {row.recipientFirstName} {row.recipientLastName}
                                </span>
                                <span className="admin-order-dish" title={row.dishName}>{row.dishName}</span>
                                <button
                                  type="button"
                                  className="admin-order-expand"
                                  onClick={() => setSelectedOrder(row)}
                                  aria-label="Details anzeigen"
                                >
                                  +
                                </button>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === 'mails' && (
        <>
          {emailLogsLoading ? (
            <div className="loading-center">
              <div className="spinner" />
              <span>Mail-Logs werden geladen…</span>
            </div>
          ) : emailLogs.length === 0 ? (
            <div className="admin-table-wrapper">
              <div className="admin-empty">Keine Mail-Einträge vorhanden</div>
            </div>
          ) : (
            <div className="admin-table-wrapper">
              <div className="admin-table-header mail-grid">
                {['Datum/Uhrzeit', 'Typ', 'Für Datum', 'Status', 'Gericht 1', 'Gericht 2', 'Stornierungen', 'Fehlermeldung'].map((h) => (
                  <div key={h} className="admin-th">{h}</div>
                ))}
              </div>
              {emailLogs.map((log) => (
                <div key={log.id} className="admin-row mail-grid">
                  <div className="admin-cell" data-label="Datum/Uhrzeit">{formatSentAt(log.sentAt)}</div>
                  <div className="admin-cell" data-label="Typ">{log.type === 'weekly' ? 'Woche' : 'Täglich'}</div>
                  <div className="admin-cell" data-label="Für Datum">{log.forDate}</div>
                  <div className="admin-cell" data-label="Status">
                    {log.status === 'sent' && (
                      <span style={{ color: 'var(--success-text)', fontWeight: 600 }}>Gesendet</span>
                    )}
                    {log.status === 'skipped' && (
                      <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Nicht nötig</span>
                    )}
                    {log.status === 'failed' && (
                      <span style={{ color: 'var(--error-text)', fontWeight: 700 }}>Fehler</span>
                    )}
                  </div>
                  <div className="admin-cell" data-label="Gericht 1">
                    {log.dish1Name ? `${log.dish1Count != null ? `(${log.dish1Count}) ` : ''}${log.dish1Name}` : '—'}
                  </div>
                  <div className="admin-cell" data-label="Gericht 2">
                    {log.dish2Name ? `${log.dish2Count != null ? `(${log.dish2Count}) ` : ''}${log.dish2Name}` : '—'}
                  </div>
                  <div className="admin-cell" data-label="Stornierungen">{log.cancellations != null ? log.cancellations : '—'}</div>
                  <div className="admin-cell" data-label="Fehlermeldung" title={log.errorMessage}>
                    {log.errorMessage ? (
                      <span style={{ color: 'var(--error-text)', fontSize: '0.8em' }}>{log.errorMessage}</span>
                    ) : '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'billing' && (
        <div style={{ maxWidth: '32rem' }}>
          <h2 style={{ marginTop: 0, marginBottom: '1.5rem', fontSize: '1.25rem', fontWeight: 700 }}>Abrechnungs-Export</h2>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>Monat</label>
            <input
              type="month"
              className="admin-filter-input"
              value={billingMonth}
              onChange={(e) => setBillingMonth(e.target.value)}
              style={{ width: '12rem' }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <button
              className="btn btn-accent-hover"
              onClick={handleDownloadBilling}
              disabled={billingLoading}
            >
              {billingLoading ? 'Wird erstellt…' : 'Abrechnungs-CSVs herunterladen (ZIP)'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleDownloadReconciliation}
              disabled={reconciliationLoading}
            >
              {reconciliationLoading ? 'Wird erstellt…' : 'Abgleichs-CSV herunterladen'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleDownloadCostCenterBilling}
              disabled={costCenterLoading}
            >
              {costCenterLoading ? 'Wird erstellt…' : 'Kostenstellen-Abrechnung herunterladen (XLSX)'}
            </button>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 0 }}>
            Die ZIP-Datei enthält eine CSV pro Unternehmen. Die Abgleichs-CSV enthält Bestellungen, Stornierungen und Nachbestellungen pro KW und Unternehmen.
            Die Kostenstellen-Abrechnung zieht die Kostenstelle pro Mitarbeiter live aus Dynamics und gruppiert die Gerichte-Anzahl pro Firma und Kostenstelle.
          </p>
        </div>
      )}

      {showIngestion && (
        <MenuIngestionModal
          onClose={() => setShowIngestion(false)}
          onSaved={loadData}
          addToast={addToast}
        />
      )}

      {selectedOrder && (
        <div className="modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div className="modal-card" style={{ maxWidth: '24rem' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <div className="modal-title">
                  {selectedOrder.recipientFirstName} {selectedOrder.recipientLastName}
                </div>
                <div className="modal-subtitle">{selectedOrder.dishName}</div>
              </div>
              <button className="modal-close" onClick={() => setSelectedOrder(null)} aria-label="Schließen" />
            </div>
            <div className="modal-body">
              {pickupEnabled && (
                <div className="admin-detail-row">
                  <span className="admin-detail-label">Abholcode</span>
                  <span className="admin-detail-value" style={{ fontFamily: 'monospace', fontWeight: 700 }}>
                    {selectedOrder.pickupCode || '—'}
                  </span>
                </div>
              )}
              {pickupEnabled && (
                <div className="admin-detail-row">
                  <span className="admin-detail-label">Abgeholt</span>
                  <span className="admin-detail-value">{selectedOrder.redeemed ? 'Ja' : 'Nein'}</span>
                </div>
              )}
              <div className="admin-detail-row">
                <span className="admin-detail-label">Status</span>
                <span className="admin-detail-value">
                  {selectedOrder.order.cancelled
                    ? <span style={{ color: 'var(--error-text)', fontWeight: 700 }}>Storniert</span>
                    : <span style={{ color: 'var(--success-text)', fontWeight: 600 }}>Aktiv</span>}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
